// Shared handshake for the /print/preview.html popup, used by both the live-form and
// stored-snapshot print paths. Injecting on the mere existence of #preview races the
// window's own setup and leaves the spinner up forever when it lands early.

import { useNotificationStore } from '@/stores/notification';

const PREVIEW_WINDOW_FEATURES = 'menubar=no,location=no,resizeable=yes,scrollbars=yes,status=yes';

// A form document is styled by print-document.css, which owns the printed record and loads no app
// CSS. The preview window reads ?doc=1 in its <head> to pick that sheet over print-preview.css,
// which the paths that clone live app DOM into this window still need. Any path printing the form
// print tree — live form or stored snapshot — opens the window with this.
export const DOCUMENT_PREVIEW_PATH = '/print/preview.html?doc=1';

// The same window on the app's stylesheet, for the documents that are app DOM rather than the
// print tree: the services report's cloned v-data-table, and the hand-authored statement and
// receipt, which have always been laid out against Vuetify's base typography.
export const APP_PREVIEW_PATH = '/print/preview.html';

const FAILURE_MESSAGES = {
    'popup-blocked': 'Your browser blocked the print preview window. Allow pop-ups for this site, then try printing again.',
    'preview-timeout': 'The print preview window did not finish loading. Close it and try printing again.',
    'preview-closed': 'The print preview window was closed before the document could be sent to it.',
};

// Returns null on a blocked popup rather than throwing; callers pass that straight to
// renderToPreviewWindow, which is the only place a print failure is reported to the user.
export function openPreviewWindow(previewPath = APP_PREVIEW_PATH) {
    return window.open(previewPath, '_blank', PREVIEW_WINDOW_FEATURES) || null;
}

// parsePage gates both signals: without it, a window whose print-preview.js failed to load
// reports ready and then throws on injection. bnPreviewReady is the primary signal, readyState
// the fallback for a cached preview.html predating it; a throwing .document read is not-ready.
function isPreviewReady(previewWindow) {
    try {
        if (typeof previewWindow.parsePage !== 'function') return false;
        return previewWindow.bnPreviewReady === true || previewWindow.document?.readyState === 'complete';
    } catch {
        return false;
    }
}

// Rejects with a distinct reason instead of polling forever, so the user is told which
// of the three failures happened.
export function waitForPreviewReady(previewWindow, { timeout = 15000, interval = 50 } = {}) {
    return new Promise((resolve, reject) => {
        // A null window can never become ready, so it settles before any polling starts.
        if (!previewWindow) {
            reject(new Error('popup-blocked'));
            return;
        }

        // True once the outcome is known. Shared by the first check and the poll so the
        // readiness rules can't drift between them; an already-ready window costs no tick.
        const settled = () => {
            if (previewWindow.closed) {
                reject(new Error('preview-closed'));
                return true;
            }
            if (isPreviewReady(previewWindow)) {
                resolve();
                return true;
            }
            return false;
        };

        if (settled()) return;

        const startedAt = Date.now();
        const poll = setInterval(() => {
            if (settled()) {
                clearInterval(poll);
                return;
            }
            if (Date.now() - startedAt >= timeout) {
                clearInterval(poll);
                reject(new Error('preview-timeout'));
            }
        }, interval);
    });
}

function notifyPreviewFailure(error, config) {
    const reason = error?.message || 'preview-failed';
    console.error('Print preview failed:', reason, config?.id ? 'config id: ' + config.id : '');
    useNotificationStore().add({
        message: FAILURE_MESSAGES[reason] || 'The print preview could not be opened. Please try again.',
        color: 'error',
        timeout: 8000,
    });
}

// The single place a print-preview failure reaches the user. Never rejects: callers do not
// await it, so a rejection here would be a silent unhandled one behind a stuck spinner.
export async function renderToPreviewWindow(previewWindow, config, html, themeStyling) {
    try {
        await waitForPreviewReady(previewWindow);
        // Inside the try on purpose — parsePage itself can throw.
        previewWindow.parsePage(config, html, themeStyling);
    } catch (error) {
        notifyPreviewFailure(error, config);
        return false;
    }
    return true;
}
